import { useEffect } from "react";
import {
	Control,
	Controller,
	ControllerFieldState,
	ControllerProps,
	ControllerRenderProps,
	FieldPath,
	FieldValues,
	UseFormStateReturn,
} from "react-hook-form";
import KOGAutoCompleteCheckbox, { IKOGAutoCompleteCheckboxProps, IOption } from ".";
import { AutocompleteChangeReason } from "@mui/material";
import { renderErrorMessages } from "@/main/services/utils";

type IControllerProps = Omit<ControllerProps, "render" | "name">;
interface IKogInputAutoCompleteCheckControlledProps<T extends FieldValues>
	extends Omit<IKOGAutoCompleteCheckboxProps, "setValueCustom"> {
	control: Control<T, object>;
	name: FieldPath<T>;
	controllerProps?: IControllerProps;
	errorMessageCustom?: string;
	isDefaultValueUnique?: boolean;
	keysOptionsDisabled?: (string | number)[];
	setValueCustom?: (name: FieldPath<T>, value: IOption[]) => void;
	showOnlyQuantityLabel?: boolean;
	disableVirtualization?: boolean;
}

function KogInputAutoCompleteCheckControlled<T extends FieldValues>(
	props: IKogInputAutoCompleteCheckControlledProps<T>,
) {
	const {
		control,
		controllerProps,
		errorMessageCustom,
		name,
		onChangeCustom = () => {},
		isDefaultValueUnique = true,
		setValueCustom = () => {},
		...propsInput
	} = props;

	const options = propsInput.options || [];

	function handleSetValueCustom(value: IOption[]) {
		if (setValueCustom) {
			setValueCustom(name, value);
		}
	}

	const renderInput = ({
		field,
		fieldState: { error },
	}: {
		field: ControllerRenderProps<FieldValues, string>;
		fieldState: ControllerFieldState;
		formState: UseFormStateReturn<FieldValues>;
	}) => {
		const onChangeInput = (value: IOption[], optionChanged: IOption | undefined, reason: AutocompleteChangeReason) => {
			onChangeCustom(value, optionChanged, reason);
			field.onChange(value);
		};

		const baseInputProps: IKOGAutoCompleteCheckboxProps = {
			...propsInput,
			...field,
		};

		return (
			<KOGAutoCompleteCheckbox
				{...baseInputProps}
				errorMessageCustom={errorMessageCustom || renderErrorMessages(props.label, error)}
				options={options}
				valueCustom={field.value || []}
				onChangeCustom={onChangeInput}
				setValueCustom={handleSetValueCustom}
			/>
		);
	};

	const customControllerProps: IControllerProps = {
		...controllerProps,
	};

	useEffect(() => {
		if (options.length === 1 && setValueCustom && isDefaultValueUnique) {
			setValueCustom(name, [options[0]]);
		}
	}, [JSON.stringify(options), isDefaultValueUnique]);

	return (
		<Controller control={control as Control<FieldValues>} name={name} {...customControllerProps} render={renderInput} />
	);
}

export default KogInputAutoCompleteCheckControlled;
